import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from '@store';
import {ICoveragePlan, IPlanToSave, ISnapshot} from '@types';
import {plansAPI} from '@api/methods';
import {IDLE, PENDING} from '@constants/loadingState';

export interface CoveragePlansState {
  plans: ICoveragePlan[];
  loading: string;
  currentPlan: ISnapshot | null;
  editedCells: Record<number, {coverage: number; auto_coverage: number}>;
}

const initialState: CoveragePlansState = {
  plans: [],
  loading: IDLE,
  currentPlan: null,
  editedCells: {},
};

export const getPlans = createAsyncThunk<ICoveragePlan[], number, {state: RootState}>(
  'plan/get/all',
  async systemId => {
    return await plansAPI.getPlans(systemId);
  },
);

export const addPlan = createAsyncThunk<
  string,
  {
    planData: IPlanToSave;
    systemId: number;
  },
  {state: RootState}
>('plan/add', async ({planData, systemId}) => {
  return await plansAPI.addPlan({planData, systemId});
});

export const getPlan = createAsyncThunk<
  any,
  {plan_id: number; system_id: number},
  {state: RootState}
>('plan/get/one', async ({plan_id, system_id}) => {
  return await plansAPI.getPlan({plan_id, system_id});
});

export const editPlanCells = createAsyncThunk<any, {plan_id: number}, {state: RootState}>(
  'plan/edit',
  async ({plan_id}, {getState}) => {
    const state = getState();
    const editedCells = state.coveragePlans.editedCells;
    const cells = Object.entries(editedCells).map(([id, v]) => ({
      id: Number(id),
      coverage: v.coverage,
      auto_coverage: v.auto_coverage,
    }));
    return await plansAPI.editPlan({plan_id, planData: {cells}});
  },
);

export const deletePlan = createAsyncThunk<string, {plan_id: number}, {state: RootState}>(
  'plan/delete',
  async ({plan_id}) => {
    return await plansAPI.deletePlan(plan_id);
  },
);

const coveragePlansSlice = createSlice({
  name: 'coveragePlans',
  initialState,
  reducers: {
    removeCoveragePlan(state, action: PayloadAction<number>) {
      state.plans = state.plans.filter(p => p.id !== action.payload);
    },
    clearCoveragePlans(state) {
      state.plans = [];
    },
    setCurrentPlan(state, action: PayloadAction<ISnapshot | null>) {
      state.currentPlan = action.payload;
    },
    addTemporaryPlanCell(
      state,
      action: PayloadAction<{
        subsystem_id: number;
        module_id: number;
        test_type_id: number;
        coverage: number;
        auto_coverage: number;
      }>,
    ) {
      if (!state.currentPlan) return;
      const {subsystem_id, module_id, test_type_id, coverage, auto_coverage} =
        action.payload;
      const subsystem = state.currentPlan.subsystems.find(s => s.id === subsystem_id);
      const module = subsystem?.modules.find(m => m.id === module_id);
      const cell = module?.cells?.find(c => c.test_type_id === test_type_id);
      if (!cell) return;
      cell.coverage = coverage;
      cell.auto_coverage = auto_coverage;
      state.editedCells[cell.id] = {coverage, auto_coverage};
    },
    clearTemporaryPlanEdits(state) {
      state.editedCells = {};
    },
  },
  extraReducers: builder => {
    builder.addCase(getPlans.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(getPlans.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(getPlans.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.plans = payload;
    });
    builder.addCase(getPlan.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(getPlan.rejected, state => {
      state.loading = IDLE;
      state.currentPlan = null;
    });
    builder.addCase(getPlan.fulfilled, (state, {payload}) => {
      state.loading = IDLE;
      state.currentPlan = payload;
      state.editedCells = {};
    });
    builder.addCase(addPlan.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(addPlan.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(addPlan.fulfilled, state => {
      state.loading = IDLE;
    });
    builder.addCase(editPlanCells.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(editPlanCells.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(editPlanCells.fulfilled, state => {
      state.loading = IDLE;
      state.editedCells = {};
    });
    builder.addCase(deletePlan.pending, state => {
      state.loading = PENDING;
    });
    builder.addCase(deletePlan.rejected, state => {
      state.loading = IDLE;
    });
    builder.addCase(deletePlan.fulfilled, (state, action) => {
      state.loading = IDLE;
      const deletedId = Number(action.meta.arg.plan_id);
      state.plans = state.plans.filter(p => p.id !== deletedId);
      if (state.currentPlan && (state.currentPlan as any).id === deletedId) {
        state.currentPlan = null;
        state.editedCells = {};
      }
    });
  },
});

export const selectCoveragePlans = (state: RootState) => state.coveragePlans.plans;
export const selectCurrentPlan = (state: RootState) => state.coveragePlans.currentPlan;
export const selectEditedPlanCells = (state: RootState) =>
  state.coveragePlans.editedCells;

export const {
  removeCoveragePlan,
  clearCoveragePlans,
  setCurrentPlan,
  addTemporaryPlanCell,
  clearTemporaryPlanEdits,
} = coveragePlansSlice.actions;

export default coveragePlansSlice.reducer;
